import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../core/permissions/permissions.service';

/**
 * Wezel uprawnien pulpitu. Dostep nadaje sie w panelu (Administracja ->
 * Profile -> Uprawnienia) - zadnych list w kodzie.
 *
 * Wczesniej (27.08, pilotaz po awarii) byla tu twarda lista loginow. Kazde
 * "wlacz jeszcze X" oznaczalo commit i dwa deploye, wiec poszlo na smietnik.
 */
const DASHBOARD_RESOURCE_KEY = 'dashboard';

/**
 * Auto-odswiezanie: 5 minut, nie 60 sekund jak w v1. Po awarii 27.08 nie ma
 * powodu, zeby domyslna zakladka odpytywala backend co minute u kazdego przez
 * caly dzien - dane pulpitu to liczby zamowien, ktore nie zmieniaja sie
 * z minuty na minute na tyle, zeby ktos to zauwazyl. Dodatkowo odswiezamy
 * TYLKO gdy karta jest widoczna: zapomniana zakladka w tle nie generuje ruchu.
 */
const REFRESH_MS = 5 * 60_000;

type Bucket = 'shipped' | 'ready' | 'inProgress' | 'pending' | 'cancelled';

interface GroupRow {
  name?: string;
  login?: string;
  total: number;
  shipped: number;
  ready: number;
  inProgress: number;
  pending: number;
  cancelled: number;
}

interface BacklogOrder {
  id: number;
  date: string;
  label: string;
  bucket: Bucket;
}

interface IssueOrder {
  id: number;
  label: string;
  importError: boolean;
  toClarify: boolean;
}

interface SideStats {
  windowFrom: string;
  windowTo: string;
  total: number;
  fromPrevDay: number;
  afterCutoff: number;
  lastWeekTotal: number;
  buckets: Record<Bucket, number>;
  issues: number;
  toClarify: number;
  backlog: number;
  backlogOrders?: BacklogOrder[];
  issuesOrders?: IssueOrder[];
  channels?: GroupRow[];
  managers?: GroupRow[];
}

interface FlowChannel { total: number; open: number; }

interface FlowHour {
  hour: number;
  prevDay: boolean;
  b2c: FlowChannel;
  b2b: FlowChannel;
}

interface FlowData {
  hours: FlowHour[];
  peak: number;
  nowHour: number;
  nowLabel: string | null;
  cutoffDefault: number;
  /**
   * Pula startowa: niespakowane z POPRZEDNIEGO dnia roboczego - stos, z ktorym
   * magazyn zaczyna dzien. Godzina splywu tamtego dnia nie ma znaczenia.
   */
  startPool: {
    date: string;
    /** ile z puli JESZCZE lezy */
    open: number;
    openB2c: number;
    openB2b: number;
    /** ile z puli magazyn domknal w tym dniu */
    done: number;
    /** ile bylo na start dnia (open + done) */
    total: number;
  };
}

interface DashboardDay {
  date: string;
  isToday: boolean;
  /** sobota/niedziela na zywo: okno poniedzialku narasta; `monday` = data, do ktorej zaliczy sie to okno */
  weekendPreview: { monday: string } | null;
  generatedAt: string;
  cutoffs: { b2b: string; b2c: string };
  b2c: SideStats;
  b2b: SideStats;
  /** trzecia strona: serwis (kanaly 8/20/36-38) - Hubert 31.08 */
  service: SideStats;
  hourly: FlowData;
}

/** Wybór granicy spływu; null = "bez granicy" (żadna kolumna nie jest alarmowa). */
const FLOW_CUTOFF_STORAGE_KEY = 'home.flowCutoffHour';

interface StatRow {
  labelKey: string;
  subKey: string;
  count: number;
  p: number;
  color: string;
}

/**
 * Pulpit dnia — ilościowy obraz zamówień "na tapecie" dnia roboczego.
 * Wygląd: wariant "B — Kokpit" z Claude Design (wybrany kierunek, 27.08) —
 * łuk postępu dnia, pierścienie statusów, lollipopy kanałów. Tylko sztuki,
 * zero kwot. Auto-odświeżanie co 60 s przy widoku dzisiejszym.
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.sass'],
})
export class HomeComponent implements OnInit, OnDestroy {
  readonly minDate = '2026-07-01';
  readonly maxDate = this.toDateString(new Date());

  data: DashboardDay | null = null;
  loading = false;
  selectedDate = this.maxDate;
  /** licznik hero animowany rAF-em przy pełnym ładowaniu (nie przy cichym) */
  displayTotal = 0;
  /**
   * Sterowanie animacją rysowania łuku/pierścieni: SVG startuje "puste"
   * (pełny dashoffset), po chwili dostaje docelowy offset i CSS transition
   * go dorysowuje. Liczone w TS, bo Angular 14 wycina custom properties
   * (--p) z [ngStyle] — wartość nie dociera do CSS i calc() w animacji
   * jest nieważny (objaw: koła zawsze w 100% zamalowane).
   */
  chartsReady = false;
  readonly gaugeDash = 251.3;
  readonly ringDash = 150.8;
  /** rozwinięcia list: zaległe / problemy (cel pulpitu: wygaszanie takich zamówień) */
  backlogOpen = false;
  issuesOpen = false;
  /**
   * Granica spływu na wysyłkę dzisiejszą; null = "bez granicy".
   * Miękka i przestawialna - zapamiętywana lokalnie w przeglądarce, bo to
   * preferencja patrzącego, nie ustawienie firmowe (i zero ruchu do backendu).
   */
  cutoffHour: number | null = 13;
  readonly cutoffOptions = [12, 13, 14, 15];

  private refreshTimer: any = null;
  private countUpRaf: number | null = null;
  private chartsTimer: any = null;

  constructor(
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly permissions: PermissionsService,
  ) {}

  /**
   * Widok zastepczy zamiast pulpitu, gdy user nie ma uprawnienia `dashboard`.
   * Ustawiane po dojsciu mapy uprawnien, a nie w inicjalizatorze pola -
   * mapa jest asynchroniczna, a pole policzone za wczesnie pokazaloby
   * "brak dostepu" kazdemu przy pierwszym renderze.
   */
  disabled = false;

  ngOnInit(): void {
    this.restoreCutoff();
    // Mapa uprawnien dociera asynchronicznie (PermissionsService.refresh()
    // startuje w AppComponent), a Home montuje sie natychmiast - stad
    // czekanie na `ready`, zamiast czytac pusta mape przy pierwszym renderze
    // i pokazac "brak dostepu" komus, kto dostep ma.
    this.permissions.ensureLoaded().subscribe(() => {
      this.disabled = !this.permissions.has(DASHBOARD_RESOURCE_KEY);
      if (this.disabled) {
        this.loading = false;
        return;
      }
      this.start();
    });
  }

  private restoreCutoff(): void {
    try {
      const saved = localStorage.getItem(FLOW_CUTOFF_STORAGE_KEY);
      if (saved === 'none') this.cutoffHour = null;
      else if (saved && !isNaN(Number(saved))) this.cutoffHour = Number(saved);
    } catch {
      // brak dostępu do storage - zostaje domyślne 13
    }
  }

  private start(): void {
    this.load(this.selectedDate);
    this.refreshTimer = setInterval(() => {
      // document.hidden: karta schowana w tle nie odpytuje backendu.
      if (this.data?.isToday && !this.loading && !document.hidden) {
        this.load(this.selectedDate, true);
      }
    }, REFRESH_MS);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.countUpRaf) cancelAnimationFrame(this.countUpRaf);
    if (this.chartsTimer) clearTimeout(this.chartsTimer);
  }

  onDateChange(value: string): void {
    if (!value) return;
    const dow = new Date(`${value}T12:00:00`).getDay();
    // miniony weekend -> poniedzialek; DZISIEJSZY weekend przechodzi (podglad
    // narastajacego okna poniedzialku, Hubert 14.09)
    if ((dow === 0 || dow === 6) && value !== this.maxDate) {
      this.snackBar.open(this.translate.instant('HOME.WEEKEND_INFO'), 'OK', { duration: 6000 });
      return;
    }
    this.selectedDate = value;
    this.load(value);
  }

  goToday(): void {
    this.selectedDate = this.maxDate;
    this.load(this.selectedDate);
  }

  private load(date: string, silent = false): void {
    // Jedyna brama do HTTP - guard tutaj lapie tez recznie zmieniona date
    // i przycisk "dzis", nie tylko wejscie na zakladke.
    if (this.disabled) return;
    if (!silent) this.loading = true;
    this.http.get<DashboardDay>('/dashboard/day', { params: { date } }).subscribe({
      next: (res) => {
        this.data = res;
        this.loading = false;
        if (silent) {
          this.displayTotal = this.combinedTotal;
          this.chartsReady = true;
        } else {
          this.countUp(this.combinedTotal);
          this.chartsReady = false;
          if (this.chartsTimer) clearTimeout(this.chartsTimer);
          this.chartsTimer = setTimeout(() => (this.chartsReady = true), 80);
        }
      },
      error: (err) => {
        this.loading = false;
        this.snackBar.open(
          err?.error?.message ?? this.translate.instant('HOME.LOAD_ERROR'),
          'OK',
          { duration: 6000 },
        );
      },
    });
  }

  private countUp(end: number): void {
    if (this.countUpRaf) cancelAnimationFrame(this.countUpRaf);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.displayTotal = end;
      return;
    }
    const dur = 900;
    const t0 = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      const ease = 1 - Math.pow(1 - p, 3);
      this.displayTotal = Math.round(end * ease);
      if (p < 1) this.countUpRaf = requestAnimationFrame(step);
    };
    this.displayTotal = 0;
    this.countUpRaf = requestAnimationFrame(step);
  }

  // ------------------------------------------------------------ agregaty

  combined(key: Bucket): number {
    if (!this.data) return 0;
    return this.data.b2b.buckets[key] + this.data.b2c.buckets[key]
      + (this.data.service?.buckets[key] ?? 0);
  }

  /** wszystkie trzy strony w jednym miejscu - do sum i list */
  private get sides(): SideStats[] {
    if (!this.data) return [];
    return [this.data.b2c, this.data.b2b, this.data.service].filter(Boolean) as SideStats[];
  }

  get combinedTotal(): number {
    return this.sides.reduce((s, side) => s + side.total, 0);
  }

  get todayOnly(): number {
    return this.combinedTotal - this.combinedFromPrevDay;
  }

  get combinedFromPrevDay(): number {
    return this.sides.reduce((s, side) => s + side.fromPrevDay, 0);
  }

  get combinedAfterCutoff(): number {
    return this.sides.reduce((s, side) => s + side.afterCutoff, 0);
  }

  get combinedBacklog(): number {
    return this.sides.reduce((s, side) => s + side.backlog, 0);
  }

  get combinedIssues(): number {
    return this.sides.reduce((s, side) => s + side.issues + side.toClarify, 0);
  }

  get combinedLastWeek(): number {
    return this.sides.reduce((s, side) => s + side.lastWeekTotal, 0);
  }

  /** zamknięte = wysłane + gotowe (spakowana robota magazynu) */
  get closedCount(): number {
    return this.combined('shipped') + this.combined('ready');
  }

  get progressDenom(): number {
    return this.combinedTotal - this.combined('cancelled');
  }

  get progressPct(): number {
    return this.progressDenom > 0 ? Math.round((this.closedCount / this.progressDenom) * 100) : 0;
  }

  /** łuk zewnętrzny (wysłane+gotowe) i wewnętrzny (same wysłane), 0..1 */
  get gaugeClosedP(): number {
    return this.progressDenom > 0 ? this.closedCount / this.progressDenom : 0;
  }

  get gaugeShippedP(): number {
    return this.progressDenom > 0 ? this.combined('shipped') / this.progressDenom : 0;
  }

  get statRows(): StatRow[] {
    const denom = this.progressDenom;
    const p = (n: number) => (denom > 0 ? n / denom : 0);
    return [
      { labelKey: 'HOME.TILE_SHIPPED', subKey: 'HOME.SHIPPED_SUB', count: this.combined('shipped'), p: p(this.combined('shipped')), color: 'var(--dash-shipped)' },
      { labelKey: 'HOME.TILE_READY', subKey: 'HOME.READY_SUB', count: this.combined('ready'), p: p(this.combined('ready')), color: 'var(--dash-ready)' },
      { labelKey: 'HOME.TILE_IN_PROGRESS', subKey: 'HOME.PROGRESS_SUB', count: this.combined('inProgress'), p: p(this.combined('inProgress')), color: 'var(--dash-progress)' },
      { labelKey: 'HOME.TILE_PENDING', subKey: 'HOME.PENDING_SUB', count: this.combined('pending'), p: p(this.combined('pending')), color: 'var(--dash-pending)' },
    ];
  }

  pct(p: number): number {
    return Math.round(p * 100);
  }

  /** docelowy stroke-dashoffset łuku/pierścienia (pełny, dopóki nie chartsReady) */
  dashOffset(circumference: number, p: number): number {
    return this.chartsReady ? circumference * (1 - Math.max(0, Math.min(1, p))) : circumference;
  }

  delta(side: SideStats): number {
    return side.total - side.lastWeekTotal;
  }

  get nonZeroChannels(): GroupRow[] {
    return (this.data?.b2c.channels ?? []).filter((c) => c.total > 0);
  }

  get zeroChannels(): GroupRow[] {
    return (this.data?.b2c.channels ?? []).filter((c) => c.total === 0);
  }

  get maxChannelTotal(): number {
    return Math.max(1, ...this.nonZeroChannels.map((c) => c.total));
  }

  channelTitle(ch: GroupRow): string {
    return `${ch.name}: ${ch.total} — ${this.translate.instant('HOME.LEGEND_SHIPPED')} ${ch.shipped}, `
      + `${this.translate.instant('HOME.LEGEND_READY')} ${ch.ready}, `
      + `${this.translate.instant('HOME.LEGEND_IN_PROGRESS')} ${ch.inProgress}, `
      + `${this.translate.instant('HOME.LEGEND_PENDING')} ${ch.pending}`;
  }

  // ── Spływ godzinowy i realizacja (projekt 31.08) ───────────────────────
  // Karta odpowiada na trzy pytania naraz: ile wpłynęło w danej godzinie,
  // ile z tego jest już spakowane, i z których godzin coś jeszcze leży.

  get flow(): FlowData | null {
    return this.data?.hourly ?? null;
  }

  get flowHours(): FlowHour[] {
    return this.flow?.hours ?? [];
  }

  get flowPeak(): number {
    return Math.max(1, this.flow?.peak ?? 1);
  }

  /** wysokość słupka kanału w % (wspólna skala dla B2C i B2B) */
  barHeight(ch: FlowChannel): number {
    return (ch.total / this.flowPeak) * 100;
  }

  /**
   * Alarm: zamówienie spłynęło PRZED granicą i wciąż nie jest spakowane.
   * Godziny poprzedniego dnia są z reguły wyłączone; "bez granicy"
   * (cutoffHour === null) nie maluje niczego na czerwono.
   */
  isBeforeCutoff(h: FlowHour): boolean {
    return this.cutoffHour !== null && !h.prevDay && h.hour < this.cutoffHour;
  }

  /** liczba nad kolumną - tylko dla godzin szczytowych */
  showHourMark(h: FlowHour): boolean {
    const total = h.b2c.total + h.b2b.total;
    return total > 0 && total >= this.flowPeak;
  }

  hourTotal(h: FlowHour): number {
    return h.b2c.total + h.b2b.total;
  }

  /** klasa komórki paska zaległości */
  stripClass(h: FlowHour): string {
    const total = h.b2c.total + h.b2b.total;
    const open = h.b2c.open + h.b2b.open;
    if (total === 0) return 'e';
    if (open === 0) return h.prevDay ? 'p' : 'z';
    return this.isBeforeCutoff(h) ? 'o pre' : 'o';
  }

  stripLabel(h: FlowHour): string {
    const total = h.b2c.total + h.b2b.total;
    const open = h.b2c.open + h.b2b.open;
    if (total === 0) return '';
    return open === 0 ? '✓' : String(open);
  }

  barTitle(h: FlowHour, side: 'b2c' | 'b2b'): string {
    const ch = h[side];
    const label = side === 'b2c' ? 'B2C' : 'B2B';
    const hh = `${String(h.hour).padStart(2, '0')}:00`;
    const state = ch.open === 0
      ? this.translate.instant('HOME.FLOW_STATE_DONE')
      : this.translate.instant('HOME.FLOW_STATE_OPEN', { n: ch.open });
    const prev = h.prevDay ? ` (${this.translate.instant('HOME.FLOW_PREV_DAY')})` : '';
    return `${hh}${prev} ${label} — ${ch.total} / ${state}`;
  }

  /** trzy liczby pod wykresem, liczone z danych a nie z opisów */
  get flowSummary(): { before: number; done: number; open: number; openAll: number } {
    let before = 0;
    let done = 0;
    let open = 0;
    let openAll = 0;
    for (const h of this.flowHours) {
      const total = h.b2c.total + h.b2b.total;
      const o = h.b2c.open + h.b2b.open;
      openAll += o;
      if (this.isBeforeCutoff(h)) {
        before += total;
        done += total - o;
        open += o;
      }
    }
    return { before, done, open, openAll };
  }

  get startPoolTitle(): string {
    const p = this.flow?.startPool;
    if (!p) return '';
    if (!p.total) return this.translate.instant('HOME.FLOW_STARTPOOL_EMPTY');
    return this.translate.instant('HOME.FLOW_STARTPOOL_TITLE', {
      b2c: p.openB2c, b2b: p.openB2b, done: p.done, total: p.total, date: p.date,
    });
  }

  /** ile procent puli startowej magazyn juz przerobil */
  get startPoolPct(): number {
    const p = this.flow?.startPool;
    if (!p?.total) return 0;
    return Math.round((p.done / p.total) * 100);
  }

  onCutoffChange(value: string): void {
    this.cutoffHour = value === 'none' ? null : Number(value);
    try {
      localStorage.setItem(FLOW_CUTOFF_STORAGE_KEY, value);
    } catch {
      // tryb prywatny / zablokowane storage - wybór działa do przeładowania
    }
  }

  get cutoffValue(): string {
    return this.cutoffHour === null ? 'none' : String(this.cutoffHour);
  }

  get backlogList(): (BacklogOrder & { side: string })[] {
    if (!this.data) return [];
    return [
      ...(this.data.b2b.backlogOrders ?? []).map((o) => ({ ...o, side: 'B2B' })),
      ...(this.data.b2c.backlogOrders ?? []).map((o) => ({ ...o, side: 'B2C' })),
      ...(this.data.service?.backlogOrders ?? []).map((o) => ({ ...o, side: 'SRW' })),
    ].sort((a, b) => a.date.localeCompare(b.date));
  }

  get issuesList(): (IssueOrder & { side: string })[] {
    if (!this.data) return [];
    return [
      ...(this.data.b2b.issuesOrders ?? []).map((o) => ({ ...o, side: 'B2B' })),
      ...(this.data.b2c.issuesOrders ?? []).map((o) => ({ ...o, side: 'B2C' })),
      ...(this.data.service?.issuesOrders ?? []).map((o) => ({ ...o, side: 'SRW' })),
    ];
  }

  daysOld(date: string): number {
    if (!this.data) return 0;
    const ms = new Date(`${this.data.date}T12:00:00`).getTime() - new Date(`${date}T12:00:00`).getTime();
    return Math.max(0, Math.round(ms / 86_400_000));
  }

  orderHref(id: number): string {
    return `${location.pathname}#/orders/edit/${id}`;
  }

  bucketKey(bucket: Bucket): string {
    const map: Record<Bucket, string> = {
      shipped: 'HOME.LEGEND_SHIPPED',
      ready: 'HOME.LEGEND_READY',
      inProgress: 'HOME.LEGEND_IN_PROGRESS',
      pending: 'HOME.LEGEND_PENDING',
      cancelled: 'HOME.TILE_PENDING',
    };
    return map[bucket] ?? 'HOME.LEGEND_PENDING';
  }

  managerLabel(login: string | undefined): string {
    if (!login || login === 'BEZ_OPIEKUNA') return this.translate.instant('HOME.NO_MANAGER');
    if (login === 'KarolPietka') return 'Karol';
    if (login === 'DorotaOleksy') return 'Dorota';
    return login;
  }

  get generatedTime(): string {
    if (!this.data) return '';
    return new Date(this.data.generatedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  }

  private toDateString(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }
}
